import {
	ChangeEvent,
	FC,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import InputBase from "@mui/material/InputBase";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PreviewIcon from "@mui/icons-material/Preview";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import AuditLogModal from "../../components/modals/AuditLog";
import ConfigRowActions from "../../components/ConfigListShell/ConfigRowActions";
import getIcon from "../../utils/get-icon";
import * as metrics from "./designMetrics";
import ActionEditor, { type ActionDraft } from "./ActionEditor";
import GroupEditor, { type GroupDraft } from "./GroupEditor";
import {
	codeChipSx,
	statusPillSx,
	typeChipSx,
	ungroupedHeaderSx,
	ungroupedSectionSx,
	warningBadgeSx,
} from "./typeChipStyles";
import ConfigDeleteDialog from "../../components/ConfigListShell/ConfigDeleteDialog";
import { useGetRoles } from "../../hooks/useRoles";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import { grantingRoleNames } from "./actionGrants";
import ConfigListStates, {
	type ListState,
} from "../../components/ConfigListShell/ConfigListStates";
import ConfigTable, {
	type ConfigColumn,
} from "../../components/ConfigListShell/ConfigTable";
import ConfigCards from "../../components/ConfigListShell/ConfigCards";
import ConfigListToolbar, {
	ConfigListViewControls,
} from "../../components/ConfigListShell/ConfigListToolbar";
import {
	useConfigList,
	STATUS_ALL,
} from "../../components/ConfigListShell/useConfigList";
import {
	ActionRequest,
	ActionUpdateRequest,
	useCreateAction,
	useDeleteAction,
	useGetAllActions,
	useUpdateAction,
} from "../../hooks/useActions";
import {
	ActionGroupRequest,
	ActionGroupUpdateRequest,
	useCreateActionGroup,
	useDeleteActionGroup,
	useGetActionGroups,
	useGetAllActionGroups,
	useGetRolesPreview,
	useReorderActionGroups,
	useUpdateActionGroup,
} from "../../hooks/useActionGroups";
import {
	IAction,
	resolveActionGroupId,
	resolveActionGroupName,
} from "../../domain/action.interface";
import {
	IActionGroup,
	RolesPreviewSection,
} from "../../domain/action-group.interface";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { useUserStore } from "../../stores/user.store";
import { apiErrorMessage } from "../../utils/apiErrorMessage";

const ACTION_TYPES = [
	"MENU_OPTION",
	"KPI",
	"ALERTS",
	"OPTION",
	"EVENTS",
	"SPECIAL_FEATURE",
	"REPORT",
	"System",
];

/**
 * Names a tab's panel for assistive technology without drawing a second title.
 *
 * The screen shows ONE title, in the shared header, as the frames do. But each panel still
 * needs a heading: it is what a screen-reader user lands on when the tab changes, and it is
 * what `openGroupsTab` and the unit suite locate to prove the right panel rendered. Deleting
 * the visible h5s removed that too — so the heading stays, clipped rather than absent.
 *
 * Not `display: none`: that would take it out of the accessibility tree along with the
 * layout, which is the opposite of the intent.
 */
const visuallyHidden = {
	position: "absolute" as const,
	width: "1px",
	height: "1px",
	padding: 0,
	margin: "-1px",
	overflow: "hidden",
	clip: "rect(0 0 0 0)",
	whiteSpace: "nowrap" as const,
	border: 0,
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, { label: "All", value: 1000 }];

/**
 * SIMS-8678 AC3 + AC-STATES (FR-9): loading, load-failed, no-search-results and first-run
 * empty must read as FOUR DIFFERENT things. The screen this replaces showed a single
 * "No actions found." row for the last two, so an administrator could not tell an empty
 * catalog from a search that simply missed.
 */
const ACTION_LIST_COPY = {
	loadFailedTitle: "Actions couldn't be loaded",
	loadFailedBody:
		"The request failed before anything was returned. Nothing has been changed.",
	emptyTitle: "No actions yet",
	emptyBody:
		"An action is one permission a role can grant. Create a group first, then add the actions that belong in it.",
	loadingLabel: "Loading actions…",
	noResultsTitle: (query: string) => `No actions match “${query}”`,
	noResultsBody:
		"No action name, code or description contains that text with the current type filter.",
	addLabel: "Create the first group",
};

type ManagementTab = "actions" | "groups";

const createEmptyGroupForm = (createdBy: string): ActionGroupRequest => ({
	name: "",
	description: "",
	icon: "",
	status: "ACTIVE",
	createdBy,
});

const ActionsComponent: FC = () => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const actorId = userInfo?.id || "SYSTEM_USER";

	const [activeTab, setActiveTab] = useState<ManagementTab>("actions");
	const [showActionForm, setShowActionForm] = useState(false);
	const [editingAction, setEditingAction] = useState<IAction | null>(null);

	const [groupSearchInput, setGroupSearchInput] = useState("");
	const [groupSearch, setGroupSearch] = useState("");
	const [groupPage, setGroupPage] = useState(0);
	const [groupPageSize, setGroupPageSize] = useState(10);
	const [showGroupForm, setShowGroupForm] = useState(false);
	const [editingGroup, setEditingGroup] = useState<IActionGroup | null>(null);
	const [groupForm, setGroupForm] = useState<ActionGroupRequest>(
		createEmptyGroupForm(actorId),
	);
	/**
	 * SIMS-8730 AC2 — the group's membership, edited from the GROUP side.
	 *
	 * Membership lives on the ACTION (`action.groupId`), not on the group, so there is nothing
	 * on `ActionGroupUpdateRequest` to carry it. Held here as the ids the user has selected, and
	 * reconciled on save by updating the actions that changed — which is the only place the
	 * relationship actually exists.
	 */
	const [groupMemberIds, setGroupMemberIds] = useState<string[]>([]);
	// The membership as it was when the editor opened. The editor compares against it to
	// know whether a membership-only edit is a change — see GroupEditor's initialMemberIds.
	const [initialGroupMemberIds, setInitialGroupMemberIds] = useState<string[]>(
		[],
	);
	const [groupDraft, setGroupDraft] = useState<IActionGroup[]>([]);
	const [hasUnsavedOrder, setHasUnsavedOrder] = useState(false);
	const [movedGroupIdSet, setMovedGroupIdSet] = useState<Set<string>>(
		new Set(),
	);
	// `preview` is a third VIEW of the Groups tab, not a mode flag: the design's view track
	// carries Table / Cards / Roles preview, and selecting one swaps what the tab renders.
	const [groupView, setGroupView] = useState<"table" | "cards" | "preview">(
		"table",
	);
	const [openAuditLogModal, setOpenAuditLogModal] = useState(false);
	const [pendingTabSwitch, setPendingTabSwitch] =
		useState<ManagementTab | null>(null);
	const [actionPendingDelete, setActionPendingDelete] =
		useState<IAction | null>(null);

	const allActionsQuery = useGetAllActions();
	const groupsQuery = useGetActionGroups(
		groupSearch || undefined,
		groupPage + 1,
		groupPageSize,
	);
	const allGroupsQuery = useGetAllActionGroups();
	const rolesPreviewQuery = useGetRolesPreview(groupView === "preview");
	/**
	 * SIMS-8762 AC1 — the roles that grant each action, for the delete guard.
	 *
	 * Read up front rather than on demand: `GET /roles` returns every role with its granted
	 * action ids, so the answer is already in the browser by the time a delete is attempted and
	 * the dialog can open straight into the right state. Fetching it on click would show a
	 * "checking" state for a lookup that needs no round trip.
	 */
	const rolesQuery = useGetRoles();
	const createAuditlogMutation = useCreateAuditlog();

	const createActionMutation = useCreateAction();
	const updateActionMutation = useUpdateAction();
	const deleteActionMutation = useDeleteAction();
	const createGroupMutation = useCreateActionGroup();
	const updateGroupMutation = useUpdateActionGroup();
	const deleteGroupMutation = useDeleteActionGroup();
	const reorderGroupsMutation = useReorderActionGroups();

	useEffect(() => {
		// SIMS-8734 — the guard is load-bearing, not a micro-optimisation.
		//
		// Without it this effect runs ON MOUNT and, 300ms later, clears `hasUnsavedOrder` —
		// so a reorder made inside that window was silently discarded and the draft banner
		// vanished with it. C17659 caught it: the move landed sooner than in the other tests
		// and the order snapped back to the saved one with nothing on screen to say why.
		//
		// Returning early when the settled term already matches the input means the debounce
		// only ever runs for a real search change, which is the only case that should reset a
		// draft (a new result set makes the pending positions meaningless).
		if (groupSearchInput === groupSearch) {
			return;
		}
		const timeoutId = setTimeout(() => {
			setGroupSearch(groupSearchInput);
			setGroupPage(0);
			setHasUnsavedOrder(false);
			setMovedGroupIdSet(new Set());
		}, 300);
		return () => clearTimeout(timeoutId);
	}, [groupSearchInput, groupSearch]);

	const groups = useMemo(
		() => groupsQuery.data?.body?.data || [],
		[groupsQuery.data],
	);
	const allGroups = useMemo(
		() => allGroupsQuery.data?.body?.data || [],
		[allGroupsQuery.data],
	);
	const activeGroups = useMemo(
		() => allGroups.filter((group) => group.status === "ACTIVE"),
		[allGroups],
	);
	const groupsById = useMemo(
		() => new Map(allGroups.map((group) => [group._id, group])),
		[allGroups],
	);

	useEffect(() => {
		if (!hasUnsavedOrder) {
			setGroupDraft(groups);
		}
	}, [groups, hasUnsavedOrder]);

	const editingActionId = editingAction?._id;
	/**
	 * FR-17 — only navigation entries can be chosen as children, and an action can never be its
	 * own child. The editor owns the SELECTED set now; this is just the offerable one.
	 */
	const subActionOptions = useMemo<IAction[]>(() => {
		const all = allActionsQuery.data?.body?.data || [];
		return all.filter(
			(candidate) =>
				candidate.type === "MENU_OPTION" && candidate._id !== editingActionId,
		);
	}, [allActionsQuery.data, editingActionId]);

	/**
	 * The group an action reads as. "Ungrouped" is a REAL, permanent value here, not a
	 * placeholder for missing data (FR-12b) — which is why sorting by group must place these
	 * rows rather than drop them (case C17747).
	 */
	const actionGroupLabel = useCallback(
		(action: IAction): string => {
			const groupId = resolveActionGroupId(action.groupId);
			return (
				resolveActionGroupName(action.groupId) ||
				(groupId ? groupsById.get(groupId)?.name : undefined) ||
				"Ungrouped"
			);
		},
		[groupsById],
	);

	/**
	 * SIMS-8678 — the Actions list runs on the Foundation epic's shared list shell, which the
	 * story asks for by name ("Reuses the list shell established in the Foundation epic") and
	 * which the Payers screen already uses. The shell owns search, type filter, sort, view mode
	 * and pagination together, over the WHOLE record set.
	 *
	 * That whole-set requirement (FR-2, AC3/AC5) is why the rows come from `useGetAllActions`
	 * rather than the paged `useGetActions`: the paged endpoint only ever hands the browser one
	 * page, so searching or sorting it would cover the page and silently miss the rest. It is
	 * also why the sort is not a `sortBy`/`sortOrder` query parameter — `GET /actions` accepts
	 * none, and inventing them would have made the E2E green while the deployed screen did not
	 * reorder.
	 */
	const allActionRows = useMemo<IAction[]>(
		() => allActionsQuery.data?.body?.data || [],
		[allActionsQuery.data],
	);

	/**
	 * AC2 applies to BOTH tabs, so Groups gets the same view toggle. It deliberately does NOT
	 * get `useConfigList`'s sorting: AC6 requires the groups list to stay in its configured
	 * order — the order role administrators read (FR-20/FR-23) — so re-sorting it would
	 * misrepresent the thing the list exists to show.
	 */

	const actionList = useConfigList<IAction>({
		rows: allActionRows,
		// AC3: name, description AND code — the three fields the AC names.
		searchableFields: (action) => [
			action.name,
			action.description,
			action.code,
		],
		// AC4 reuses the shell's status slot for the action TYPE filter: it is the one
		// single-select narrowing control this screen has, and the shell resets the page on
		// change, which AC-PAGINATION requires.
		statusOf: (action) => action.type,
		sortValue: (action, key) => {
			if (key === "group") return actionGroupLabel(action);
			if (key === "order") return action.order ?? 0;
			return String((action as unknown as Record<string, unknown>)[key] ?? "");
		},
		// AC5: "the default ordering is code ascending" (case C17745).
		defaultSortKey: "code",
	});

	/**
	 * Which of the four list states the body is in. ORDER MATTERS: a slow fetch must never
	 * read as "no actions" and a genuinely empty catalog must never read as "no search
	 * results" — that three-way distinction is AC3 and AC-STATES (FR-9). Same ordering the
	 * Payers screen uses, deliberately.
	 */
	const actionListState: ListState = allActionsQuery.isLoading
		? "loading"
		: allActionsQuery.isError
			? "load-failed"
			: allActionRows.length === 0
				? "empty"
				: actionList.total === 0
					? "no-results"
					: "ready";

	/**
	 * SIMS-8773 — the audit-log entry point.
	 *
	 * The story asks for the "audit-log placement established in the Foundation epic", which is
	 * the Payers screen's icon-only control on the list header: a 40x40 brand-outlined secondary
	 * button (`.c-btn--secondary`, card-system.css:336-347) opening the shared modal. Copied from
	 * there deliberately rather than restyled, so the two configuration screens carry the same
	 * control in the same place.
	 *
	 * It sits on BOTH tab headers. AC2 and AC3 cover changes made from the group side as well as
	 * the action side (cases C17615, C17616), and both tabs are views onto ONE catalog, so a log
	 * reachable from only one of them would be missing from wherever the auditor happened to be.
	 * One modal, one module — the tab is not part of the query.
	 */
	/**
	 * SIMS-8773 (PRD FR-7) — record a catalogue change. NEVER THROWS.
	 *
	 * The audit write is a SEPARATE call made after the record has already been saved, so its
	 * failure says nothing about whether the user's change succeeded. Letting it reject would
	 * report a saved action as unsaved and leave the editor holding input that is already
	 * persisted — the user's next move is to press Save again and create a duplicate. Failing
	 * silently for the USER is right; failing silently for OPERATORS is not, so the error still
	 * reaches the console.
	 *
	 * The codes are declared in the backend's `ActionCode` enum, which validates them with
	 * `@IsEnum`. They were added deliberately BEFORE this wiring: sending a code the enum lacks
	 * returns 400 while the save succeeds, which is how every payer audit write failed unnoticed
	 * for eight months (cmi-frontend 706db985). `action-code.audit.spec.ts` pins both families
	 * so adding a write here without a code there fails that suite.
	 *
	 * NO PHI, and nothing that could become it (TR-05 / NFR-4). Every detail below names
	 * configuration only — action codes, group names, counts — never a member, visit or
	 * participant.
	 */
	const postAuditlog = async (
		actionCode: string,
		action: string,
		detail: string,
	): Promise<void> => {
		try {
			await createAuditlogMutation.mutateAsync({
				appType: "WEB_BACK_OFFICE",
				module: "Actions",
				action,
				detail,
				actionCode,
				option: "Actions Management",
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(
				`[Actions] audit log failed (${actionCode}) — the catalogue change itself succeeded`,
				error,
			);
		}
	};

	const renderAuditLogButton = (): JSX.Element => (
		<Tooltip title="Audit log">
			<IconButton
				aria-label="Audit log"
				onClick={() => setOpenAuditLogModal(true)}
				sx={{
					width: "2.5rem",
					height: "2.5rem",
					borderRadius: "0.5rem",
					border: "1px solid",
					borderColor: "primary.main",
					color: "primary.main",
					backgroundColor: "background.paper",
					"&:hover": {
						backgroundColor: (t) => alpha(t.palette.primary.main, 0.06),
					},
				}}
			>
				<HistoryOutlinedIcon sx={{ fontSize: "1.25rem" }} />
			</IconButton>
		</Tooltip>
	);

	/**
	 * The row's actions, as a kebab — shared by the table row and the card so the two views
	 * behave alike.
	 *
	 * The design gives every row a 32px `more_vert` button opening a 180px menu with "Edit
	 * action" and a destructive "Delete" (`Actions Pattern v2.dc.html:290`). The build had a
	 * strip of two icon buttons, which is a different affordance: it spends the row's right
	 * edge on controls that are used rarely, and it gives the destructive one equal weight
	 * with the safe one instead of putting it behind a deliberate second click.
	 *
	 * `ConfigRowActions` already existed in the shell for exactly this and already matched
	 * the design's metrics — it simply was not wired up here.
	 */
	const renderActionRowActions = (action: IAction): JSX.Element => (
		<Box display="flex" justifyContent="flex-end">
			<ConfigRowActions
				recordName={action.name}
				actions={[
					{
						label: "Edit action",
						icon: <EditIcon fontSize="small" />,
						onClick: () => openEditActionForm(action),
					},
					{
						label: "Delete",
						icon: <DeleteIcon fontSize="small" />,
						onClick: () => deleteAction(action),
						destructive: true,
					},
				]}
			/>
		</Box>
	);

	/**
	 * AC5 names exactly five sortable columns — code, name, type, group, order. "Sub-actions"
	 * and the row-actions column are deliberately NOT sortable; adding a sixth would be
	 * inventing a requirement.
	 */
	const actionColumns: ConfigColumn<IAction>[] = [
		{
			key: "code",
			header: "Code",
			width: "16%",
			sortable: true,
			render: (action) => (
				<Chip label={action.code} size="small" sx={codeChipSx} />
			),
		},
		{
			key: "name",
			header: "Name",
			width: "24%",
			sortable: true,
			/*
			 * actions-01..07 set the name in medium weight with the description beneath it in
			 * muted 12px, clipped to one line. The list showed the name alone, so the only
			 * place a description appeared was the editor — an administrator scanning 89 rows
			 * to find the right permission had nothing to tell two similar names apart.
			 */
			render: (action) => (
				<Box>
					<Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
						{action.name}
					</Typography>
					{action.description && (
						<Typography
							sx={{
								fontSize: "0.75rem",
								color: "text.secondary",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{action.description}
						</Typography>
					)}
				</Box>
			),
		},
		{
			key: "type",
			header: "Type",
			width: "14%",
			sortable: true,
			render: (action) => (
				<Box>
					{/*
					 * A record with NO type at all rendered as a bare empty chip — a 20px
					 * coloured blob with nothing in it, which says less than an empty cell
					 * would. Seen on live data: the AVG_* analytics actions carry no `type`.
					 *
					 * FR-24 is about reporting what is stored rather than correcting it, so the
					 * value is not invented here; "Not set" names the absence instead, and the
					 * "Unrecognised type" line below still applies because an absent type is
					 * not one of the reference values either.
					 */}
					<Chip
						label={action.type || "Not set"}
						size="small"
						sx={typeChipSx(action.type)}
					/>
					{/*
					 * SIMS-8710 AC1 (FR-24) — a stored type outside the reference list is marked
					 * HERE TOO, not only in the editor.
					 *
					 * Hi-Fi actions-08-unrecognised-type puts this line under the type chip in the
					 * list row, and the list carried nothing: an administrator scanning the
					 * catalogue saw DASH_WIDGET sitting among MENU_OPTION and KPI with no sign
					 * that it is not one of them, and had to open the record to find out.
					 *
					 * In WORDS with an icon, never colour alone (NFR-5). The value itself is still
					 * shown unchanged above — the point of FR-24 is that the screen reports what is
					 * stored rather than correcting it.
					 */}
					{!ACTION_TYPES.includes(action.type) && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								mt: 0.5,
							}}
						>
							{/*
							 * ERROR, not warning. The design marks an unrecognised type with the
							 * `error` glyph in `--error-dark` (`Actions Pattern v2.dc.html:276`),
							 * and pairs the chip itself with the error fill. This read as a
							 * warning — a different statement about the record, not just a
							 * different colour: a warning says "look at this", an error says
							 * "this value is not one we know". FR-24's point is the second.
							 */}
							<ErrorOutlineIcon
								sx={{ fontSize: "0.75rem", color: "error.dark" }}
							/>
							<Typography
								sx={{
									fontSize: "0.625rem",
									fontWeight: 600,
									color: "error.dark",
								}}
							>
								Unrecognised type
							</Typography>
						</Box>
					)}
				</Box>
			),
		},
		{
			key: "group",
			header: "Group",
			width: "18%",
			sortable: true,
			/*
			 * The frames put the GROUP's icon beside its name, which is how the column reads
			 * as sections rather than as free text — the same glyph the group carries on the
			 * Groups tab and in the permission preview.
			 *
			 * Ungrouped gets no glyph on purpose: it is a real, permanent value (FR-12b) but
			 * it is not a configured section, and giving it one would imply it were.
			 */
			render: (action) => {
				const groupId = resolveActionGroupId(action.groupId);
				const icon = groupId ? groupsById.get(groupId)?.icon : undefined;
				return (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
						{icon && (
							<Box
								sx={{
									/*
									 * The SIMS purple, not a neutral.
									 *
									 * This was changed AWAY from the brand colour on my own reasoning
									 * — "the column is a label, not a call to action" — and that was
									 * wrong: the design's `--icons-main` for SIMS is #8A54B2, a brand
									 * mid-tone, and every glyph in these frames is purple.
									 *
									 * `primary.main` is the closest role that holds a brand purple.
									 * The exact mid-tone has no role at all, which is gap 7 of the
									 * design escalation and why this is close rather than exact.
									 */
									display: "flex",
									color: "primary.main",
									"& svg": { fontSize: "1.125rem" },
								}}
							>
								{getIcon(icon)}
							</Box>
						)}
						<Typography sx={{ fontSize: "0.875rem" }}>
							{actionGroupLabel(action)}
						</Typography>
					</Box>
				);
			},
		},
		{
			key: "order",
			header: "Order",
			width: "8%",
			sortable: true,
			render: (action) => (action.type === "MENU_OPTION" ? action.order : "—"),
		},
		{
			key: "subActions",
			header: "Sub-actions",
			width: "12%",
			sortable: false,
			// The frames show the bare count ("2"), not "2 sub-actions" — the column header
			// already says what is being counted, and repeating it in every row crowded a
			// narrow column.
			render: (action) => action.subActions?.length || "—",
		},
		{
			key: "actions",
			header: "",
			width: "8%",
			sortable: false,
			align: "right",
			render: (action) => renderActionRowActions(action),
		},
	];

	const groupTotalCount = groupsQuery.data?.body?.pagination?.totalCount || 0;

	/**
	 * SIMS-8734 AC1 — WHICH group the user moved, not merely that something did.
	 *
	 * Hi-Fi actions-10 marks one row ("Moved — not saved") and leaves the rest alone, and the
	 * case asks for exactly that: "only the moved group is marked".
	 *
	 * Tracked from the ARROW the user pressed, deliberately, rather than derived by comparing
	 * positions against the saved list. A swap displaces TWO rows, so a positional comparison
	 * marks both — which is true but not what the export shows and not what the user did. This
	 * names the action, not its side effects.
	 */
	const movedGroupIds = useMemo<Set<string>>(
		() => (hasUnsavedOrder ? movedGroupIdSet : new Set<string>()),
		[hasUnsavedOrder, movedGroupIdSet],
	);

	// SIMS-8702 AC3 (FR-12b): the Ungrouped section belongs to the preview only when
	// there are ungrouped actions to put in it. It used to be appended unconditionally,
	// so an account with every action classified showed an empty "No actions" box that
	// the Hi-Fi never had — the design renders the section outside the group loop AND
	// conditional on `group === null` records existing. The API is the source of truth:
	// if it returns no ungrouped bucket, there is nothing ungrouped.
	const previewSections = useMemo<RolesPreviewSection[]>(
		() => rolesPreviewQuery.data || [],
		[rolesPreviewQuery.data],
	);

	/**
	 * SIMS-8734 AC4 — the preview shows the PENDING order.
	 *
	 * The preview exists so the author can judge an order before committing it, so showing the
	 * saved one would defeat it. The endpoint answers with the saved order (it knows nothing
	 * about a draft held in this browser), so the draft is applied here: sections are sorted by
	 * their position in `groupDraft`, and anything the draft does not know about — including the
	 * Ungrouped section, which belongs to no group — keeps its place at the end.
	 */
	const draftOrderedPreview = useMemo<RolesPreviewSection[]>(() => {
		if (!hasUnsavedOrder) return previewSections;
		const draftAt = new Map(
			groupDraft.map((group, index) => [group._id, index]),
		);
		const rank = (section: RolesPreviewSection): number =>
			section.groupId && draftAt.has(section.groupId)
				? (draftAt.get(section.groupId) as number)
				: Number.MAX_SAFE_INTEGER;
		return [...previewSections].sort((a, b) => rank(a) - rank(b));
	}, [hasUnsavedOrder, previewSections, groupDraft]);

	const closeActionEditor = (): void => {
		setShowActionForm(false);
		setEditingAction(null);
	};

	const openCreateActionForm = (): void => {
		setEditingAction(null);
		setShowActionForm(true);
	};

	const openEditActionForm = (action: IAction): void => {
		setEditingAction(action);
		setShowActionForm(true);
	};

	/**
	 * SIMS-8687 — the editor's save.
	 *
	 * THROWS on failure, deliberately. The shell shows the reason in its save-failed banner and
	 * keeps every entered value (C17731 / FR-5); swallowing the rejection into a snackbar, as the
	 * inline form did, left the banner empty and the editor looking as though the save had
	 * worked. `useConfigEditor` only advances its saved snapshot on success, so a failed save
	 * also leaves the draft dirty rather than pretending it landed.
	 */
	const submitAction = async (draft: ActionDraft): Promise<void> => {
		try {
			if (editingAction) {
				const request: ActionUpdateRequest = {
					// AC3 (C17717/C17718): the code is NOT sent on an update. It is immutable, it is
					// what role grants reference, and omitting it means the screen cannot be the
					// thing that changes it even by accident.
					name: draft.name,
					description: draft.description,
					type: draft.type,
					groupId: draft.groupId,
					link: draft.link,
					order: draft.order,
					icon: draft.icon,
					subActions: draft.subActions,
					_id: editingAction._id,
					updatedBy: actorId,
				} as ActionUpdateRequest;
				await updateActionMutation.mutateAsync(request);
				// SIMS-8773 AC2 (C17613/C17635) — a MOVE is not an edit. A regrouping is recorded
				// under its own code and names BOTH groups, because the two have different
				// consequences for whoever held the permission and an auditor has to tell them
				// apart. "No group" is a real origin (FR-12b), not missing data, so it is spelled
				// out rather than left blank.
				const previousGroupId =
					resolveActionGroupId(editingAction.groupId) || "";
				if (previousGroupId !== draft.groupId) {
					const nameOf = (id: string): string =>
						allGroups.find((group) => group._id === id)?.name ?? "no group";
					await postAuditlog(
						"WEB_ACTION_REGROUP",
						"When regrouping",
						`Moved action ${editingAction.code} from ${nameOf(previousGroupId)} to ${nameOf(draft.groupId)}`,
					);
				} else {
					await postAuditlog(
						"WEB_ACTION_UPD",
						"When updating",
						`Updated action ${editingAction.code}`,
					);
				}
				showSnackBar("Action updated successfully", "success");
			} else {
				const request: ActionRequest = {
					code: draft.code,
					name: draft.name,
					description: draft.description,
					type: draft.type,
					groupId: draft.groupId,
					link: draft.link,
					order: draft.order,
					icon: draft.icon,
					subActions: draft.subActions,
					createdBy: actorId,
				};
				await createActionMutation.mutateAsync(request);
				await postAuditlog(
					"WEB_ACTION_ADD",
					"When creating",
					`Created action ${draft.code} in ${
						allGroups.find((group) => group._id === draft.groupId)?.name ??
						"no group"
					}`,
				);
				showSnackBar("Action created successfully", "success");
			}
			closeActionEditor();
		} catch (error) {
			throw new Error(
				apiErrorMessage(
					error,
					editingAction ? "Failed to update action" : "Failed to create action",
				),
			);
		}
	};

	/**
	 * SIMS-8762 — open the delete dialog. The guard itself lives in what the dialog is given.
	 *
	 * This replaced a `window.confirm` that named the record and stopped there: it stated no
	 * consequence (FR-8) and knew nothing about grants, so deleting an action a role still
	 * granted was refused only by the backend, as a snackbar after the fact — by which point the
	 * administrator had already confirmed a destructive action believing it safe.
	 */
	const deleteAction = (action: IAction): void => {
		setActionPendingDelete(action);
	};

	const confirmDeleteAction = async (): Promise<void> => {
		const action = actionPendingDelete;
		if (!action) return;
		try {
			await deleteActionMutation.mutateAsync(action._id);
			// C17635: a deletion must not read as "moved to no group". A deletion REVOKES; a move
			// does not. Its own code, and the wording says what happened.
			await postAuditlog(
				"WEB_ACTION_DELETE",
				"When deleting",
				`Deleted action ${action.code}`,
			);
			setActionPendingDelete(null);
			showSnackBar("Action deleted successfully", "success");
		} catch (error) {
			// The dialog stays open on failure: closing it would leave the administrator with a
			// snackbar and no idea whether the record is gone.
			showSnackBar(
				apiErrorMessage(
					error,
					"Failed to delete action. It may still be assigned to roles.",
				),
				"error",
			);
		}
	};

	const closeGroupForm = (): void => {
		setShowGroupForm(false);
		setEditingGroup(null);
		setGroupMemberIds([]);
		setInitialGroupMemberIds([]);
		setGroupForm(createEmptyGroupForm(actorId));
	};

	const openCreateGroupForm = (): void => {
		setEditingGroup(null);
		setGroupMemberIds([]);
		setInitialGroupMemberIds([]);
		setGroupForm(createEmptyGroupForm(actorId));
		setShowGroupForm(true);
	};

	const openEditGroupForm = (group: IActionGroup): void => {
		setEditingGroup(group);
		const held = allActionRows
			.filter((action) => resolveActionGroupId(action.groupId) === group._id)
			.map((action) => action._id);
		setGroupMemberIds(held);
		setInitialGroupMemberIds(held);
		setGroupForm({
			name: group.name,
			description: group.description || "",
			icon: group.icon || "",
			status: group.status,
			createdBy: actorId,
		});
		setShowGroupForm(true);
	};

	/**
	 * SIMS-8730 AC2 — apply the membership the user chose on the group editor.
	 *
	 * Two sets of writes, and both matter. Actions newly selected are moved INTO this group;
	 * actions that were in it and are no longer selected are moved OUT, to no group — which is
	 * a legitimate, permanent state (FR-12b), not a deletion.
	 *
	 * Only the DIFFERENCE is written. Re-sending every member would touch records the user did
	 * not change, and each write is a real update with its own audit consequence.
	 *
	 * DELIBERATELY NOT A WHOLESALE REWRITE OF THE MEMBERSHIP LIST. TestRail C17670/C17671 ask
	 * that two administrators editing the same group MERGE rather than overwrite; sending a
	 * per-action delta is what makes that possible at all, because one session's stale list
	 * cannot undo the other's change. Whether the SERVICE merges concurrent writes is FR-25 and
	 * is not settled here.
	 */
	const reconcileGroupMembership = async (groupId: string): Promise<void> => {
		const wasMember = new Set(
			allActionRows
				.filter((action) => resolveActionGroupId(action.groupId) === groupId)
				.map((action) => action._id),
		);
		const nowMember = new Set(groupMemberIds);

		const moves = allActionRows.filter((action) => {
			const inBefore = wasMember.has(action._id);
			const inAfter = nowMember.has(action._id);
			return inBefore !== inAfter;
		});

		for (const action of moves) {
			const from = resolveActionGroupId(action.groupId) || "";
			const to = nowMember.has(action._id) ? groupId : "";
			const nameOf = (id: string): string =>
				allGroups.find((group) => group._id === id)?.name ?? "no group";
			await updateActionMutation.mutateAsync({
				_id: action._id,
				name: action.name,
				description: action.description,
				type: action.type,
				groupId: nowMember.has(action._id) ? groupId : "",
				link: action.link ?? "",
				order: action.order,
				icon: action.icon ?? "",
				subActions: ((action.subActions ?? []) as Array<string | IAction>).map(
					(sub) => (typeof sub === "string" ? sub : sub._id),
				),
				updatedBy: actorId,
			} as ActionUpdateRequest);
			// SIMS-8730 AC2 (C17669) — a membership change made from the GROUP side is the same
			// event as one made from the action side, so it carries the same code and names both
			// groups. Recording it differently would make the log depend on which screen the
			// administrator happened to use.
			await postAuditlog(
				"WEB_ACTION_REGROUP",
				"When regrouping",
				`Moved action ${action.code} from ${nameOf(from)} to ${nameOf(to)}`,
			);
		}
	};

	/**
	 * Save from the group editor.
	 *
	 * Takes the draft rather than reading `groupForm`, because the editor owns its draft now
	 * — that is what gives it the unsaved-changes pill and the discard prompt. `groupForm` is
	 * still set from it so the audit comparisons below, which diff against `editingGroup`,
	 * keep working unchanged.
	 *
	 * It THROWS on failure. The shell's save-failed banner needs a reason, and swallowing the
	 * error here would close the editor on a save that did not happen.
	 */
	const saveGroupDraft = async (draft: GroupDraft): Promise<void> => {
		const next: ActionGroupRequest = {
			...groupForm,
			name: draft.name,
			description: draft.description,
			icon: draft.icon,
			status: draft.status,
		};
		setGroupForm(next);
		await saveGroup(next);
	};

	const saveGroup = async (form: ActionGroupRequest): Promise<void> => {
		if (!form.name.trim()) {
			throw new Error("Name is required");
		}
		try {
			if (editingGroup) {
				const request: ActionGroupUpdateRequest = {
					_id: editingGroup._id,
					name: form.name,
					description: form.description,
					icon: form.icon,
					status: form.status,
					updatedBy: actorId,
				};
				await updateGroupMutation.mutateAsync(request);
				// SIMS-8744 AC3 (C17648) — a DEACTIVATION is its own entry, not an edit. While
				// compliance item C-1 is open, the audit log is the only way to reconstruct an
				// inactive group's effect on live permissions, so the entry that marks the group
				// inactive is the one an auditor needs to find.
				if (editingGroup.status !== form.status) {
					await postAuditlog(
						"WEB_GROUP_STATUS_CHG",
						"When changing status",
						`Set group ${form.name} to ${form.status}`,
					);
				} else if (
					// ONLY when one of the group's OWN fields moved.
					//
					// Saving this editor can do two things at once: update the group record and
					// move actions in or out of it (SIMS-8730 AC2). Emitting WEB_GROUP_UPD
					// unconditionally meant a save that changed nothing but the MEMBERSHIP still
					// filed a "group updated" entry beside the regrouping — noise that makes the
					// move harder to find, and C17669 asks for the regrouping to be
					// distinguishable from other group edits. C17656's expected result puts the
					// principle plainly: the audit log stays a record of REAL changes.
					editingGroup.name !== form.name ||
					(editingGroup.description ?? "") !== (form.description ?? "") ||
					(editingGroup.icon ?? "") !== (form.icon ?? "")
				) {
					await postAuditlog(
						"WEB_GROUP_UPD",
						"When updating",
						`Updated group ${form.name}`,
					);
				}
				await reconcileGroupMembership(editingGroup._id);
				showSnackBar("Group updated successfully", "success");
			} else {
				await createGroupMutation.mutateAsync(form);
				await postAuditlog(
					"WEB_GROUP_ADD",
					"When creating",
					`Created group ${form.name} as ${form.status}`,
				);
				showSnackBar("Group created successfully", "success");
			}
			closeGroupForm();
		} catch (error) {
			showSnackBar(
				apiErrorMessage(
					error,
					editingGroup ? "Failed to update group" : "Failed to create group",
				),
				"error",
			);
		}
	};

	const deleteGroup = async (group: IActionGroup): Promise<void> => {
		// SIMS-8750 AC2: a delete confirmation must name the record AND state the
		// consequence in words — naming it alone leaves the administrator guessing what
		// "delete" costs. The Hi-Fi (actions-22-delete-group-empty) words it exactly this
		// way. NOTE: the design specifies a modal, not a native dialog; replacing
		// window.confirm is a larger UX change owned by SIMS-8750's implementation story,
		// so this fixes the wording only. Tracked as a design-conformance gap.
		if (
			!window.confirm(
				`"${group.name}" will be permanently deleted. This cannot be undone.`,
			)
		) {
			return;
		}
		try {
			await deleteGroupMutation.mutateAsync(group._id);
			await postAuditlog(
				"WEB_GROUP_DELETE",
				"When deleting",
				`Deleted group ${group.name}`,
			);
			showSnackBar("Group deleted successfully", "success");
		} catch (error) {
			showSnackBar(
				apiErrorMessage(
					error,
					"Failed to delete group. Move its actions to another group first.",
				),
				"error",
			);
		}
	};

	/**
	 * A preview section's position in the group draft.
	 *
	 * The preview reorders the SAME draft the Groups table does — one pending order, two
	 * places to change it. Anything else would let the two disagree about what is about to
	 * be saved. Returns -1 for Ungrouped, which never joins the ordering (FR-12b).
	 */
	const previewOrderIndex = (section: RolesPreviewSection): number =>
		section.groupId
			? groupDraft.findIndex((group) => group._id === section.groupId)
			: -1;

	/** Move a section from the preview, through the same path the table's arrows use. */
	const movePreviewSection = (
		section: RolesPreviewSection,
		direction: -1 | 1,
	): void => {
		const index = previewOrderIndex(section);
		if (index < 0) return;
		moveGroup(index, direction);
	};

	const moveGroup = (index: number, direction: -1 | 1): void => {
		const targetIndex = index + direction;
		if (targetIndex < 0 || targetIndex >= groupDraft.length) return;

		const reordered = [...groupDraft];
		[reordered[index], reordered[targetIndex]] = [
			reordered[targetIndex],
			reordered[index],
		];
		const orderSlots = groupDraft
			.map((group) => group.order)
			.sort((first, second) => first - second);
		setGroupDraft(
			reordered.map((group, position) => ({
				...group,
				order: orderSlots[position],
			})),
		);
		setHasUnsavedOrder(true);
		setMovedGroupIdSet((previous) => {
			const next = new Set(previous);
			next.add(groupDraft[index]._id);
			return next;
		});
	};

	const saveGroupOrder = async (): Promise<void> => {
		try {
			const draftOrderById = new Map(
				groupDraft.map((group) => [group._id, group.order]),
			);
			const orderedGroups = allGroups.length > 0 ? allGroups : groupDraft;
			await reorderGroupsMutation.mutateAsync({
				items: orderedGroups.map((group) => ({
					groupId: group._id,
					order: draftOrderById.get(group._id) ?? group.order,
				})),
				updatedBy: actorId,
			});
			// SIMS-8734 AC2 (C17654) — ONE entry for the whole reorder, matching the one batched
			// write. An entry per shifted group would bury the change the user actually made
			// among the rows that merely moved aside.
			await postAuditlog(
				"WEB_GROUP_REORDER",
				"When reordering",
				`Reordered ${orderedGroups.length} groups; new first section is ${groupDraft[0]?.name ?? "none"}`,
			);
			setHasUnsavedOrder(false);
			setMovedGroupIdSet(new Set());
			showSnackBar("Group order saved successfully", "success");
		} catch (error) {
			showSnackBar(
				apiErrorMessage(error, "Failed to save group order"),
				"error",
			);
		}
	};

	/**
	 * SIMS-8734 AC3 — abandon the draft.
	 *
	 * Restores `groupDraft` from the SAVED list rather than re-fetching: the saved order is
	 * already in hand, and a refetch would make cancelling depend on the network. Nothing was
	 * written, so there is nothing to undo server-side and no audit entry to create.
	 */
	const cancelGroupOrder = (): void => {
		setGroupDraft(groups);
		setHasUnsavedOrder(false);
		setMovedGroupIdSet(new Set());
		setPendingTabSwitch(null);
	};

	const handleTabChange = (
		_event: SyntheticEvent,
		value: ManagementTab,
	): void => {
		// SIMS-8734 AC5 (FR-4) — a tab switch is an exit, and an exit with a pending reorder
		// asks first. The draft lives on the Groups tab; switching away with it uncommitted
		// would leave the user believing they had saved, so the same discard question every
		// other exit route asks gets asked here too.
		if (hasUnsavedOrder && value !== activeTab) {
			setPendingTabSwitch(value);
			return;
		}
		setActiveTab(value);
	};

	const renderActionsTab = (): JSX.Element => (
		<Box>
			{/*
			 * No hidden heading here, unlike the Groups panel.
			 *
			 * The screen's h1 is "Actions", so a second heading of the same name would give
			 * the page two headings called "Actions" — ambiguous to a screen reader and a
			 * strict-mode collision for any locator. The Groups panel keeps its own precisely
			 * because the title does NOT change with the tab, so nothing else names it.
			 */}
			{/*
			 * The "no active group" warning belongs to ONE state, not to every group-less one.
			 *
			 * actions-05 (nothing at all) carries no banner: the empty state already says
			 * "Create a group first, then add the actions that belong in it", so a banner above
			 * it repeats the same instruction twice on one screen.
			 *
			 * It is needed everywhere else, and the first cut of this got the condition wrong.
			 * Keying it on `allActionRows.length > 0` hid it in the case C17656 covers — no
			 * actions but an INACTIVE group — where the empty state's "Create a group first" is
			 * actively false: a group exists, it just cannot hold anything. The banner's
			 * "Create an ACTIVE group" is the only accurate sentence there.
			 *
			 * A SECOND cut then also suppressed it when there is nothing at all, reasoning from
			 * actions-05 that the empty state's "Create a group first" already says it. That
			 * reasoning is sound as UX and was still wrong to apply here: AC6 / FR-13 require a
			 * guided recovery in exactly that state and C17725 asserts the "Open Groups" route,
			 * and the design frame is silent on FR-13 rather than contrary to it. A requirement
			 * the hand-off does not model is not a requirement the hand-off removes.
			 *
			 * So the condition is only about LOADING. The duplicated instruction in the
			 * nothing-at-all state is real and is raised with the design owner (escalation
			 * point 11) rather than settled by dropping a specified affordance.
			 */}
			{activeGroups.length === 0 && !allGroupsQuery.isLoading && (
				<Alert
					severity="warning"
					sx={{ mb: 2 }}
					action={
						<Button
							color="inherit"
							size="small"
							onClick={() => setActiveTab("groups")}
						>
							Open Groups
						</Button>
					}
				>
					Create an active group before adding actions.
				</Alert>
			)}
			{/*
			 * AC3 + AC4 — search and the type filter both run through the shell, so both narrow
			 * the WHOLE record set and both reset to the first page. The chips this replaces
			 * reset the page by hand at each call site, which is the kind of thing one call site
			 * eventually forgets.
			 */}
			<Box sx={metrics.filterRow}>
				<ConfigListToolbar
					query={actionList.query}
					onQueryChange={actionList.setQuery}
					searchPlaceholder="Search actions by name, description or code"
					status={actionList.status}
					onStatusChange={actionList.setStatus}
					statusOptions={[
						{ value: STATUS_ALL, label: "All types" },
						...ACTION_TYPES.map((type) => ({ value: type, label: type })),
					]}
				/>
				<Box sx={{ flex: 1 }} />
				{/*
				 * Hi-Fi actions-01 reads "35 actions" at the right edge of the filter row and
				 * actions-04 reads "Showing 0 of 35" — the same element changing form rather than
				 * appearing, so the row does not jump as you type. This is AC3's "true total of
				 * matches", and it is distinct from the pager's range label inside the frame:
				 * the pager counts the page, this counts the match set.
				 */}
				<Typography
					sx={metrics.resultCount}
					data-testid="actions-filter-summary"
				>
					{actionList.isNarrowed
						? `Showing ${actionList.total} of ${actionList.grandTotal}`
						: `${actionList.grandTotal} ${
								actionList.grandTotal === 1 ? "action" : "actions"
							}`}
				</Typography>
			</Box>
			{/*
			 * SIMS-8678 — the list body is the Foundation epic's shared shell, the same
			 * components the Payers screen uses (ConfigListStates / ConfigTable / ConfigCards).
			 * The hand-rolled MUI table this replaces had no card view (AC2), no sort (AC5) and
			 * one "No actions found." row standing in for three distinct states (AC3 / FR-9).
			 */}
			{actionListState !== "ready" ? (
				<ConfigListStates
					state={actionListState}
					view={actionList.view}
					copy={ACTION_LIST_COPY}
					emptyIcon={
						<BoltIcon sx={{ fontSize: "2.5rem", color: "text.secondary" }} />
					}
					query={actionList.query}
					onRetry={() => void allActionsQuery.refetch()}
					onClearSearch={() => actionList.setQuery("")}
					onClearAllFilters={() => {
						actionList.setQuery("");
						actionList.setStatus(STATUS_ALL);
					}}
					onAdd={() => setActiveTab("groups")}
					errorMessage={
						allActionsQuery.error instanceof Error
							? allActionsQuery.error.message
							: undefined
					}
				/>
			) : actionList.view === "table" ? (
				<ConfigTable<IAction>
					rows={actionList.visible}
					columns={actionColumns}
					rowKey={(action) => action._id}
					sortKey={actionList.sortKey}
					sortDir={actionList.sortDir}
					onSort={actionList.toggleSort}
					ariaLabel="Actions"
				/>
			) : (
				<ConfigCards<IAction>
					rows={actionList.visible}
					rowKey={(action) => action._id}
					title={(action) => action.name}
					subtitle={(action) => action.code}
					description={(action) => action.description}
					stats={(action) => [
						{ label: "Group", value: actionGroupLabel(action) },
						{
							label: "Order",
							value: action.type === "MENU_OPTION" ? action.order : "—",
						},
						{
							label: "Sub-actions",
							value: action.subActions?.length || "—",
						},
					]}
					trailing={(action) => renderActionRowActions(action)}
					onRowClick={(action) => openEditActionForm(action)}
				/>
			)}
			{/*
			 * The shared pager, matching Payers: rows-per-page, the range readout and the
			 * chevrons live together below the list. This replaced a MUI TablePagination that
			 * carried its own "Rows per page" control — two controls for one piece of state,
			 * which made the page-size selector ambiguous and let the two disagree.
			 */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 2,
					px: 2,
					py: 1.25,
					borderTop: "1px solid",
					borderTopColor: "divider",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
						Rows per page
					</Typography>
					<Box
						component="select"
						aria-label="Rows per page"
						value={actionList.pageSize}
						onChange={(event: ChangeEvent<HTMLSelectElement>) =>
							actionList.setPageSize(event.target.value)
						}
						sx={{
							height: "2rem",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: "0.5rem",
							padding: "0 0.5rem",
							fontSize: "0.8125rem",
							backgroundColor: "background.paper",
							color: "text.primary",
						}}
					>
						{actionList.pageSizeOptions.map((option) => (
							<option key={option} value={option}>
								{option === "all" ? "All" : option}
							</option>
						))}
					</Box>
				</Box>

				<Box sx={{ flex: 1 }} />

				{/* AC3/FR-2: the TRUE total of matches, never the page length. */}
				<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
					{`${actionList.total === 0 ? 0 : actionList.rangeStart}\u2013${actionList.rangeEnd} of ${actionList.total}`}
				</Typography>

				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<IconButton
						aria-label="Previous page"
						size="small"
						disabled={actionList.page === 0}
						onClick={() => actionList.setPage(actionList.page - 1)}
					>
						<ArrowUpwardIcon
							sx={{ fontSize: "1.125rem", transform: "rotate(-90deg)" }}
						/>
					</IconButton>
					<IconButton
						aria-label="Next page"
						size="small"
						disabled={actionList.page >= actionList.pageCount - 1}
						onClick={() => actionList.setPage(actionList.page + 1)}
					>
						<ArrowDownwardIcon
							sx={{ fontSize: "1.125rem", transform: "rotate(-90deg)" }}
						/>
					</IconButton>
				</Box>
			</Box>
		</Box>
	);

	const renderGroupsTab = (): JSX.Element => (
		<Box>
			<Typography variant="h5" sx={visuallyHidden}>
				Groups
			</Typography>
			{/*
			 * The filter row, on the same shape as the Actions tab: search on the left, the
			 * record count right-aligned. The count was missing here entirely — the Actions
			 * tab has said "35 actions" at the right edge since SIMS-8678, and this tab said
			 * nothing, so narrowing the list gave no feedback about how much it narrowed.
			 */}
			<Box sx={metrics.filterRow}>
				{/*
				 * The same field the Actions tab renders through ConfigListToolbar: a bordered
				 * 40px box at radius 8, 380px wide, with the glyph inside it. This was a plain
				 * MUI TextField, which brings its own height and a 4px radius — so the two
				 * tabs of ONE screen had visibly different search boxes.
				 *
				 * Built inline rather than by reusing the toolbar, because that component also
				 * owns the status chips and the result count, and this tab needs neither.
				 */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						backgroundColor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: "0.5rem",
						height: "2.5rem",
						padding: "0 0.75rem",
						width: "23.75rem",
						maxWidth: "100%",
					}}
				>
					<SearchIcon sx={{ fontSize: "1.25rem", color: "text.secondary" }} />
					<InputBase
						value={groupSearchInput}
						onChange={(event: ChangeEvent<HTMLInputElement>) =>
							setGroupSearchInput(event.target.value)
						}
						placeholder="Search groups"
						inputProps={{ "aria-label": "Search groups" }}
						sx={{ flex: 1, fontSize: "0.875rem" }}
					/>
				</Box>
				<Box sx={{ flex: 1 }} />
				<Typography
					sx={metrics.resultCount}
					data-testid="groups-filter-summary"
				>
					{`${groupTotalCount} ${groupTotalCount === 1 ? "group" : "groups"}`}
				</Typography>
			</Box>
			{hasUnsavedOrder && (
				/*
				 * SIMS-8734 AC1/AC3 — Hi-Fi actions-10.
				 *
				 * The banner said only "Unsaved order", which named the state and left out the
				 * part that matters: role administrators go on seeing the SAVED order until this
				 * is committed. That sentence is why the draft exists (C17653), and without it a
				 * platform administrator cannot tell whether the shuffle they are looking at is
				 * already affecting anyone.
				 *
				 * CANCEL is new too. There was no way to abandon a reorder except to reload the
				 * page — AC3 asks for one, and a draft with no discard is a trap.
				 */
				/*
				 * The design's own banner (`:404`): `--warning-light` behind a
				 * `--warning-dark` hairline at radius 8, every word in `--warning-ink`, a
				 * `swap_vert` glyph, and the two buttons at the right edge — Cancel as a
				 * secondary, Save order as the primary.
				 *
				 * MUI's `severity="warning"` was close enough to look deliberate and wrong
				 * in every particular: its own amber rather than the tokens, a triangle
				 * rather than the reorder glyph, and `color="inherit"` buttons that took the
				 * Alert's text colour instead of reading as a secondary and a primary.
				 */
				<Stack
					direction="row"
					alignItems="center"
					spacing={1.5}
					sx={{
						mb: 1.5,
						backgroundColor: (theme) => theme.palette.warning.light,
						border: "1px solid",
						borderColor: (theme) => theme.palette.warning.dark,
						borderRadius: "0.5rem",
						px: 2,
						py: 1.5,
					}}
				>
					<SwapVertIcon
						sx={{
							fontSize: "1.25rem",
							color: (t) => t.palette.warningInk.main,
						}}
					/>
					<Typography
						sx={{
							fontSize: "0.8125rem",
							lineHeight: "1.125rem",
							color: (t) => t.palette.warningInk.main,
						}}
					>
						<strong>New section order not saved yet.</strong> Role
						administrators keep seeing the current order until you save.
					</Typography>
					<Box sx={{ flex: 1 }} />
					<Button
						size="small"
						variant="outlined"
						disabled={reorderGroupsMutation.isPending}
						onClick={cancelGroupOrder}
					>
						Cancel
					</Button>
					<Button
						size="small"
						variant="contained"
						disabled={reorderGroupsMutation.isPending}
						onClick={saveGroupOrder}
					>
						Save order
					</Button>
				</Stack>
			)}
			{groupView === "cards" ? (
				/*
				 * AC2 — the Groups card view. Position is READ-ONLY here: reordering stays on
				 * the table, where the up/down controls live (FR-20), which is also how the
				 * Payers card view treats it. Showing a position you cannot change beats
				 * offering a second reorder path that would have to stay in step with the first.
				 */
				<ConfigCards<IActionGroup>
					rows={groupDraft}
					rowKey={(group) => group._id}
					title={(group) => group.name}
					subtitle={(group) => `Position ${group.order}`}
					description={(group) => group.description}
					stats={(group) => [
						{
							label: "Status",
							value: group.status === "ACTIVE" ? "Active" : "Inactive",
						},
					]}
					onRowClick={(group) => openEditGroupForm(group)}
				/>
			) : groupView === "preview" ? (
				/*
				 * The roles preview as a VIEW, not a modal.
				 *
				 * The design makes it the third state of the view track — `view: 'preview'`
				 * swapping the table out in place (`Actions Pattern v2.dc.html:1301`). It was
				 * built as a Dialog, and the difference is not cosmetic: a modal covers the
				 * list you are comparing against, and the design's preview carries the section
				 * reorder arrows, so you reorder while watching what a role administrator will
				 * see. That is the whole point of a preview, and a modal cannot do it.
				 */
				<Box data-testid="roles-preview">
					{/*
					 * SIMS-8702 — what this screen IS, said out loud.
					 *
					 * Hi-Fi actions-09-roles-preview-with-ungrouped carries this line above the
					 * sections, and the dialog had nothing equivalent: it opened straight onto a
					 * list of permissions with no statement that it is a read-only rehearsal of
					 * what a role administrator sees. That is the same defect as the bare
					 * "Ungrouped" heading this story already fixed — the screen showing something
					 * true while leaving the reader to guess what it means.
					 *
					 * It matters most for the second clause. An administrator looking at a list of
					 * permissions has every reason to think they are granting them; the export
					 * says plainly that nothing is granted here.
					 */}
					{/*
					 * A tinted note, not a MUI Alert. The design draws this on
					 * `--surface-tint` inside a `--border-default` hairline (`:464`) — the
					 * same wash the section headers use, so it reads as part of the preview
					 * rather than as an interruption above it.
					 *
					 * MUI's `severity="info"` brings its own blue palette, which on a
					 * purple-branded screen is the one colour on the page belonging to no
					 * token at all.
					 */}
					<Stack
						direction="row"
						alignItems="flex-start"
						spacing={1.25}
						sx={{
							backgroundColor: (theme) => theme.palette.surfaceTint.main,
							border: "1px solid",
							borderColor: (theme) => theme.palette.outline.main,
							borderRadius: "0.5rem",
							px: 2,
							py: 1.5,
							mb: 2,
						}}
					>
						<PreviewIcon sx={{ fontSize: "1.25rem", color: "primary.main" }} />
						<Typography
							sx={{
								fontSize: "0.8125rem",
								lineHeight: "1.125rem",
								color: "text.secondary",
							}}
						>
							This is the permissions list as a role administrator sees it, in
							section order. Selection is disabled — nothing is granted from
							this screen.
						</Typography>
					</Stack>
					{rolesPreviewQuery.isLoading && (
						<Typography>Loading roles preview...</Typography>
					)}
					{rolesPreviewQuery.isError && (
						<Alert severity="error">Failed to load roles preview.</Alert>
					)}
					{!rolesPreviewQuery.isLoading && !rolesPreviewQuery.isError && (
						<Stack spacing={1.5}>
							{draftOrderedPreview.map((section) => (
								<Paper
									key={section.groupId || "ungrouped"}
									variant="outlined"
									sx={
										section.isUngrouped
											? ungroupedSectionSx
											: {
													overflow: "hidden",
													borderRadius: "0.75rem",
													borderColor: (theme) => theme.palette.outline.main,
												}
									}
								>
									{/*
									 * The section header band, as the design draws it (`:471`):
									 * reorder arrows, an INERT checkbox, the group's glyph, its
									 * name, the selected-of-total count, and an Edit group link.
									 *
									 * The checkbox is the point of the whole screen. A role
									 * administrator grants by ticking these; showing them
									 * disabled is what makes this a rehearsal of that screen
									 * rather than a list of names. It was absent entirely.
									 */}
									<Stack
										direction="row"
										alignItems="center"
										spacing={1.5}
										sx={
											section.isUngrouped
												? ungroupedHeaderSx
												: {
														backgroundColor: (theme) =>
															theme.palette.surfaceTint.main,
														borderBottom: "1px solid",
														borderColor: "divider",
														px: 2,
														py: 1.5,
													}
										}
									>
										{/*
										 * The section reorder arrows. This is what makes the
										 * preview a VIEW rather than a read-only rehearsal: you
										 * change the order while watching what a role
										 * administrator will see. They were missing entirely, so
										 * the preview could only be looked at.
										 *
										 * Ungrouped gets none — it is not a configured section
										 * and never joins the ordering (FR-12b).
										 */}
										{!section.isUngrouped && (
											<Stack sx={{ mr: -0.5 }}>
												<IconButton
													size="small"
													disabled={previewOrderIndex(section) === 0}
													aria-label={`Move ${section.name} up`}
													onClick={() => movePreviewSection(section, -1)}
													sx={{ width: "1.375rem", height: "1.125rem" }}
												>
													<KeyboardArrowUpIcon sx={{ fontSize: "1rem" }} />
												</IconButton>
												<IconButton
													size="small"
													disabled={
														previewOrderIndex(section) === groupDraft.length - 1
													}
													aria-label={`Move ${section.name} down`}
													onClick={() => movePreviewSection(section, 1)}
													sx={{ width: "1.375rem", height: "1.125rem" }}
												>
													<KeyboardArrowDownIcon sx={{ fontSize: "1rem" }} />
												</IconButton>
											</Stack>
										)}
										{!section.isUngrouped && (
											<CheckBoxOutlineBlankIcon
												sx={{ fontSize: "1.375rem", color: "outline.dark" }}
											/>
										)}
										{section.isUngrouped ? (
											<HelpOutlineIcon fontSize="small" />
										) : (
											section.icon && (
												<Box
													sx={{
														display: "flex",
														color: "primary.main",
														"& svg": { fontSize: "1.125rem" },
													}}
												>
													{getIcon(section.icon)}
												</Box>
											)
										)}
										<Typography variant="h6">{section.name}</Typography>
										{/*
										 * "0 of 4" — the selected-of-total count the Roles screen
										 * shows. Zero always, because selection is disabled here;
										 * that is the rehearsal being faithful, not a bug.
										 */}
										<Typography
											sx={{ fontSize: "0.75rem", color: "text.secondary" }}
										>
											{`0 of ${section.actions.length}`}
										</Typography>
										{/*
										 * "Moved — not saved" marks the section the user just
										 * moved, the same badge the Groups table carries. The
										 * STATUS chip that used to sit here is gone: the design's
										 * preview header does not show one, and it should not —
										 * this is a rehearsal of the Roles screen, and a role
										 * administrator never sees a group's status.
										 */}
										{section.groupId && movedGroupIds.has(section.groupId) && (
											<Chip
												size="small"
												label="Moved — not saved"
												sx={warningBadgeSx}
											/>
										)}
										<Box sx={{ flex: 1 }} />
										{!section.isUngrouped && section.groupId && (
											<Button
												size="small"
												sx={{ fontSize: "0.75rem" }}
												onClick={() => {
													const target = allGroups.find(
														(g) => g._id === section.groupId,
													);
													if (target) openEditGroupForm(target);
												}}
											>
												Edit group
											</Button>
										)}
									</Stack>
									<Box sx={section.isUngrouped ? { p: 2 } : undefined}>
										{section.isUngrouped && (
											<Typography variant="body2" color="text.secondary" mb={1}>
												These actions stay here until they are assigned to a
												group, and this section always sorts last.
											</Typography>
										)}
										{section.actions.length === 0 ? (
											/*
											 * SIMS-8766 AC3 (FR-23) — an empty section is INERT, and the
											 * author is told so rather than left to wonder. The AC's
											 * wording is kept over the design's shorter line because it
											 * states the consequence as well as the fact.
											 */
											<Typography
												variant="body2"
												color="text.secondary"
												sx={section.isUngrouped ? undefined : { p: 2 }}
											>
												This group has no actions, so it will not appear on the
												permission screen. Add an action to it and this section
												becomes visible to role administrators.
											</Typography>
										) : (
											/*
											 * TWO COLUMNS, as the design lays them out (`:491`) — and
											 * each permission carries its name, its code and its
											 * description, not a single chip reading "Name (CODE)".
											 * The description is what a role administrator actually
											 * reads to decide whether to grant it.
											 */
											<Box
												sx={{
													display: "grid",
													gridTemplateColumns: {
														xs: "1fr",
														sm: "1fr 1fr",
													},
												}}
											>
												{section.actions.map((action) => (
													<Stack
														key={action._id}
														/*
														 * Addressable as a ROW, because the name and the
														 * code are now separate elements. Nine cases
														 * asserted `getByText("Name (CODE)")` against the
														 * single string the old chip rendered; that string
														 * does not exist any more, and an assertion on the
														 * name alone would no longer prove the code is
														 * shown — which is the half a role grant actually
														 * references.
														 */
														data-testid="preview-permission"
														direction="row"
														alignItems="flex-start"
														spacing={1.25}
														sx={{
															px: 2,
															py: 1.5,
															borderBottom: "1px solid",
															borderColor: "divider",
														}}
													>
														<CheckBoxOutlineBlankIcon
															sx={{
																fontSize: "1.25rem",
																color: "outline.dark",
																mt: "1px",
															}}
														/>
														<Box sx={{ flex: 1, minWidth: 0 }}>
															<Stack
																direction="row"
																alignItems="center"
																spacing={1}
															>
																<Typography
																	sx={{
																		fontSize: "0.875rem",
																		fontWeight: 500,
																	}}
																>
																	{action.name}
																</Typography>
																<Box
																	component="span"
																	sx={{
																		fontFamily:
																			"'SF Mono', Menlo, Consolas, monospace",
																		fontSize: "0.625rem",
																		backgroundColor: (theme) =>
																			theme.palette.surface.main,
																		border: "1px solid",
																		borderColor: "divider",
																		borderRadius: "0.25rem",
																		px: "5px",
																		py: "1px",
																		color: "text.secondary",
																	}}
																>
																	{action.code}
																</Box>
															</Stack>
															{action.description && (
																<Typography
																	sx={{
																		fontSize: "0.75rem",
																		lineHeight: "1rem",
																		color: "text.secondary",
																		mt: "1px",
																	}}
																>
																	{action.description}
																</Typography>
															)}
														</Box>
													</Stack>
												))}
											</Box>
										)}
									</Box>
								</Paper>
							))}
						</Stack>
					)}
				</Box>
			) : (
				<Paper
					variant="outlined"
					/*
					 * `overflow: hidden` so the tinted header is clipped by the radius instead
					 * of painting over it. ConfigTable got this in the styles pass and this
					 * table did not, because it does not go through the shell — the same
					 * omission that left the Groups tab looking unlike the Actions tab.
					 */
					sx={{
						overflow: "hidden",
						borderRadius: "0.75rem",
						borderColor: (theme) => theme.palette.outline.main,
					}}
				>
					<TableContainer>
						{/*
						 * The same header and row treatment ConfigTable gives the Actions tab —
						 * 48px tinted uppercase header, 56px rows, a divider hairline, and a
						 * hover of the BRAND at 6% rather than MUI's neutral grey.
						 *
						 * Copied here rather than inherited, because this table does not go
						 * through the shell. That is the standing problem with this tab: every
						 * shell-level improvement misses it, and each one has to be re-applied
						 * by hand until the table is migrated (audit G9). This block is the
						 * evidence for why that migration is worth doing.
						 */}
						<Table
							sx={{
								"& thead th": {
									height: "3rem",
									backgroundColor: (theme) => theme.palette.surfaceTint.main,
									borderBottom: "1px solid",
									borderBottomColor: "divider",
									padding: "0 1rem",
									fontSize: "0.75rem",
									fontWeight: 600,
									letterSpacing: "0.04em",
									textTransform: "uppercase",
									color: "text.secondary",
									textAlign: "left",
									whiteSpace: "nowrap",
								},
								"& tbody td": {
									height: "3.5rem",
									padding: "0 1rem",
									borderBottom: "1px solid",
									borderBottomColor: "divider",
									fontSize: "0.875rem",
									color: "text.primary",
								},
								"& tbody tr:hover": {
									backgroundColor: (theme) =>
										alpha(theme.palette.primary.main, 0.06),
								},
							}}
						>
							{/*
							 * The design's columns, in its order: ORDER · GROUP · DESCRIPTION ·
							 * ACTIONS · STATUS (`Actions Pattern v2.dc.html:443`).
							 *
							 * Two changes beyond re-ordering. There is no ICON column — the glyph
							 * belongs beside the group's name, which is what makes the row read
							 * as a section rather than as a row with a picture in it. And there
							 * IS an ACTIONS column, a count of what the group holds, which the
							 * build never had: the whole point of a group is what is in it, and
							 * an administrator deciding whether a section is worth keeping could
							 * not see that without opening it.
							 *
							 * Styled here rather than through ConfigTable because this table is
							 * hand-rolled — it carries the reorder arrows and a draft order the
							 * shell has no concept of. Migrating it is its own piece of work;
							 * this brings the appearance into line without pretending to.
							 */}
							<TableHead>
								<TableRow>
									<TableCell>Order</TableCell>
									<TableCell>Group</TableCell>
									<TableCell>Description</TableCell>
									<TableCell>Actions</TableCell>
									<TableCell>Status</TableCell>
									<TableCell align="right" />
								</TableRow>
							</TableHead>
							<TableBody>
								{groupsQuery.isLoading && (
									<TableRow>
										<TableCell colSpan={6} align="center">
											Loading groups...
										</TableCell>
									</TableRow>
								)}
								{groupsQuery.isError && (
									<TableRow>
										<TableCell colSpan={6}>
											<Alert severity="error">Failed to load groups.</Alert>
										</TableCell>
									</TableRow>
								)}
								{!groupsQuery.isLoading &&
									!groupsQuery.isError &&
									groupDraft.length === 0 && (
										<TableRow>
											<TableCell colSpan={6} align="center">
												No groups found.
											</TableCell>
										</TableRow>
									)}
								{groupDraft.map((group, index) => (
									<TableRow
										key={group._id}
										/*
										 * The moved row is TINTED, not merely badged. The design
										 * washes the whole row in `--warning-light` (`:437`), which
										 * is what lets you find the pending change in a list of ten
										 * without reading every row — a 10px badge does not.
										 *
										 * The badge stays beside the name: colour is never the only
										 * signal (NFR-5), and a row tint alone would say nothing to
										 * a screen reader or in high contrast.
										 */
										sx={
											movedGroupIds.has(group._id)
												? {
														backgroundColor: (theme) =>
															theme.palette.warning.light,
													}
												: undefined
										}
									>
										<TableCell>
											<Stack direction="row" alignItems="center" spacing={0.5}>
												<Typography variant="body2">{group.order}</Typography>
												<IconButton
													size="small"
													disabled={index === 0}
													aria-label={`Move ${group.name} up`}
													onClick={() => moveGroup(index, -1)}
												>
													<ArrowUpwardIcon fontSize="small" />
												</IconButton>
												<IconButton
													size="small"
													disabled={index === groupDraft.length - 1}
													aria-label={`Move ${group.name} down`}
													onClick={() => moveGroup(index, 1)}
												>
													<ArrowDownwardIcon fontSize="small" />
												</IconButton>
											</Stack>
										</TableCell>
										<TableCell>
											<Stack direction="row" alignItems="center" spacing={1}>
												{/*
												 * The group's glyph in a rounded tile, as the frames
												 * draw it. Groups without one keep their place — the
												 * icon is optional and its absence is not a fault.
												 */}
												{group.icon && (
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															width: "2rem",
															height: "2rem",
															borderRadius: "0.5rem",
															backgroundColor: (theme) =>
																theme.palette.surfaceTwo.main,
															color: "primary.main",
															"& svg": { fontSize: "1.125rem" },
														}}
													>
														{getIcon(group.icon)}
													</Box>
												)}
												<Typography
													sx={{ fontSize: "0.875rem", fontWeight: 600 }}
												>
													{group.name}
												</Typography>
												{/*
												 * SIMS-8734 AC1 — actions-10 marks the MOVED row, and
												 * the case asks for "only the moved group". The banner
												 * says a change is pending; this says which one, so the
												 * user can see what they are about to commit instead of
												 * re-deriving it from memory.
												 */}
												{movedGroupIds.has(group._id) && (
													<Chip
														size="small"
														label="Moved — not saved"
														sx={warningBadgeSx}
													/>
												)}
											</Stack>
										</TableCell>
										<TableCell>{group.description || "—"}</TableCell>
										<TableCell>
											{
												allActionRows.filter(
													(a) => resolveActionGroupId(a.groupId) === group._id,
												).length
											}
										</TableCell>
										<TableCell>
											<Chip
												label={
													group.status === "ACTIVE" ? "Active" : "Inactive"
												}
												size="small"
												sx={statusPillSx(group.status)}
											/>
										</TableCell>
										<TableCell align="right">
											{/*
											 * The same kebab the Actions rows use, and the same
											 * one the design draws here (`:443`).
											 *
											 * The reorder ARROWS deliberately stay as visible
											 * controls, where the design puts Move up / Move down
											 * inside this menu. Reordering is the one thing on
											 * this tab a user does repeatedly and in sequence,
											 * and putting it behind two clicks per step would
											 * make a ten-group reorder twenty extra clicks. It
											 * is also what SIMS-8734's reorder cases drive. A
											 * partial, and recorded as one.
											 */}
											<ConfigRowActions
												recordName={group.name}
												actions={[
													{
														label: "Edit group",
														icon: <EditIcon fontSize="small" />,
														onClick: () => openEditGroupForm(group),
													},
													{
														label: "Delete",
														icon: <DeleteIcon fontSize="small" />,
														onClick: () => deleteGroup(group),
														destructive: true,
													},
												]}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={PAGE_SIZE_OPTIONS}
						component="div"
						count={groupTotalCount}
						rowsPerPage={groupPageSize}
						page={groupPage}
						onPageChange={(_event, newPage: number) => {
							setHasUnsavedOrder(false);
							setGroupPage(newPage);
						}}
						onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
							setHasUnsavedOrder(false);
							setGroupPageSize(Number.parseInt(event.target.value, 10));
							setGroupPage(0);
						}}
					/>
				</Paper>
			)}
		</Box>
	);

	/**
	 * SIMS-8687 — the editor is FULL PAGE, so it replaces the list rather than sitting above it.
	 *
	 * Hi-Fi actions-12/13 show it with a breadcrumb and NO tabs, which is also the same call
	 * SIMS-8614 made for Payers: rendered in place of the list rather than at its own route, so
	 * no shared routing is touched and no AC asks to deep-link an editor.
	 *
	 * It is also why C17729's fourth exit route has nothing to guard. Cancel and the breadcrumb
	 * both go through the shell's one discard prompt; the tabs are not on screen to be switched.
	 * In-app navigation cannot be intercepted either: `requestExit`'s "navigate" route is declared
	 * by SIMS-8614 but wired by nobody (Payers included), because blocking a transition needs
	 * react-router's `useBlocker`, which needs a data router — and this app mounts
	 * `<BrowserRouter>` (src/App.tsx). Recorded on the case as a Foundation-epic gap rather than
	 * fixed here: converting the app's router is not SIMS-8687's to do.
	 */
	/*
	 * The group editor is a PAGE, like the action editor beside it — not a card pushed in
	 * above the list. Same shell, so the two behave alike: one discard prompt, one
	 * unsaved-changes pill, one save-failed banner.
	 */
	if (showGroupForm) {
		return (
			<GroupEditor
				/*
				 * Keyed on the record so a different one REMOUNTS the editor.
				 *
				 * `useConfigEditor` captures its `initial` on first render, as `useState`
				 * does — so an instance kept alive across a change of record would keep the
				 * previous record's draft, and with it a bogus isDirty and a discard prompt
				 * for edits nobody made. No path reaches that today; this is what keeps it
				 * that way when one is added.
				 */
				key={editingGroup?._id ?? "new"}
				group={editingGroup}
				allActions={allActionRows}
				memberIds={groupMemberIds}
				initialMemberIds={initialGroupMemberIds}
				onMemberIdsChange={setGroupMemberIds}
				groupNameOf={actionGroupLabel}
				onSubmit={saveGroupDraft}
				onExit={closeGroupForm}
				onDelete={
					editingGroup ? () => void deleteGroup(editingGroup) : undefined
				}
			/>
		);
	}

	if (showActionForm) {
		return (
			<ActionEditor
				/*
				 * Keyed on the record so a different one REMOUNTS the editor.
				 *
				 * `useConfigEditor` captures its `initial` on first render, as `useState`
				 * does — so an instance kept alive across a change of record would keep the
				 * previous record's draft, and with it a bogus isDirty and a discard prompt
				 * for edits nobody made. No path reaches that today; this is what keeps it
				 * that way when one is added.
				 */
				key={editingAction?._id ?? "new"}
				action={editingAction}
				groups={activeGroups}
				allActions={allActionRows}
				subActionOptions={subActionOptions}
				onSubmit={submitAction}
				onExit={closeActionEditor}
				onDelete={
					editingAction ? () => void deleteAction(editingAction) : undefined
				}
			/>
		);
	}

	return (
		<Box>
			{/*
			 * ONE screen header, above the tabs — the shape actions-01..07 all use: title and
			 * count on the left, the view toggle and the primary action on the right, and the
			 * tabs BELOW that row.
			 *
			 * Each tab used to render its own copy, so the screen carried two titles ("Actions
			 * Management" over the tabs and "Actions" under them) and the controls that act on
			 * the whole screen sat inside the tab they happened to be viewed from. Moving them
			 * out is also what makes the count, the toggle and the primary button stay put when
			 * the tab changes instead of re-rendering in place.
			 *
			 * The heading KEEPS its wording. The design wants an "Actions" h1 under a "Program
			 * setup" breadcrumb, and that divergence is already recorded and annotated for the
			 * Product Owner by C-8678's own case — the string is the landing assertion of ten
			 * other specs, so renaming it is that ticket's call, not this styles pass's.
			 */}
			<Box sx={metrics.headerRow}>
				<Box>
					{/*
					 * "Program setup" — the section this screen sits in. The design leads the
					 * title with it (`Actions Pattern v2.dc.html:90`); the build had no
					 * breadcrumb at all.
					 */}
					<Typography sx={metrics.breadcrumb}>Program setup</Typography>
					{/*
					 * "Actions", not "Actions Management", and an h1 rather than an h4.
					 *
					 * The design's heading is a literal — it does NOT change with the tab,
					 * because it names the screen while the tabs choose what within it you are
					 * looking at. At 24px/32px weight 700 it is the design system's
					 * `.t-headline-sm`; MUI's h4 was neither that size nor that weight.
					 */}
					<Typography component="h1" sx={metrics.screenTitle}>
						Actions
					</Typography>
					{/*
					 * The count line the frames put under the title: the whole catalogue, not
					 * the page and not the narrowed set. It is the screen's one statement of
					 * scale, and what tells a first-run empty state from a failed load at a
					 * glance ("0 actions across 0 groups" vs no line at all).
					 */}
					<Typography sx={metrics.countLine} data-testid="actions-count-label">
						{`${allActionRows.length} ${
							allActionRows.length === 1 ? "action" : "actions"
						} across ${groupTotalCount} ${
							groupTotalCount === 1 ? "group" : "groups"
						}`}
					</Typography>
				</Box>
				<Stack direction="row" spacing={1} alignItems="center">
					{renderAuditLogButton()}
					{activeTab === "actions" ? (
						<ConfigListViewControls
							view={actionList.view}
							onViewChange={actionList.setView}
							pageSize={actionList.pageSize}
							onPageSizeChange={actionList.setPageSize}
							pageSizeOptions={actionList.pageSizeOptions}
							showPageSize={false}
						/>
					) : (
						<ConfigListViewControls
							view={groupView}
							onViewChange={(view) =>
								setGroupView(view as "table" | "cards" | "preview")
							}
							pageSize={String(groupPageSize)}
							onPageSizeChange={(size) =>
								setGroupPageSize(size === "all" ? 1000 : Number(size))
							}
							pageSizeOptions={["10", "25", "50", "all"]}
							showPageSize={false}
							/*
							 * "Roles preview" belongs INSIDE the view track, as the third segment
							 * after Table and Cards — it is another way of looking at the same
							 * groups, not a separate action, and the design groups it with them.
							 *
							 * It is a VIEW, not a dialog — `view: 'preview'` swaps the table out
							 * in place, as the design has it (`Actions Pattern v2.dc.html:1280`).
							 * The nine SIMS-8766 cases reach it through `openRolesPreview`, and
							 * leave it through `closeRolesPreview`, which selects another
							 * segment; there is no close button to click, because a view has
							 * nothing to close.
							 */
							extraControls={
								<Box
									component="button"
									type="button"
									onClick={() => setGroupView("preview")}
									aria-pressed={groupView === "preview"}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: "0.375rem",
										height: "2rem",
										padding: "0 0.75rem",
										border: "none",
										borderRadius: "0.375rem",
										cursor: "pointer",
										fontSize: "0.8125rem",
										fontWeight: 600,
										backgroundColor:
											groupView === "preview"
												? "background.paper"
												: "transparent",
										color:
											groupView === "preview"
												? "primary.main"
												: "text.secondary",
										boxShadow: groupView === "preview" ? 1 : 0,
									}}
								>
									<PreviewIcon sx={{ fontSize: "1.125rem" }} />
									Roles preview
								</Box>
							}
						/>
					)}
					{activeTab === "actions" ? (
						<Button
							variant="contained"
							startIcon={<AddIcon />}
							disabled={activeGroups.length === 0}
							onClick={openCreateActionForm}
						>
							Add Action
						</Button>
					) : (
						<Button
							variant="contained"
							startIcon={<AddIcon />}
							onClick={openCreateGroupForm}
						>
							Add Group
						</Button>
					)}
				</Stack>
			</Box>
			{/*
			 * The tabs row, with the hint right-aligned on it.
			 *
			 * The design puts `tabHint` here (`Actions Pattern v2.dc.html:126`), on the same
			 * rule as the tabs and bottom-aligned to it, not in the tab's own body. It had
			 * been sitting under the view toggle, which read as a caption for the toggle
			 * rather than for the tab it describes.
			 *
			 * It also CHANGES with the tab, which the build never did — the Groups tab had no
			 * equivalent line at all, so the one screen that explains what an action is said
			 * nothing about what a group is.
			 */}
			<Box sx={metrics.tabsRow}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					aria-label="Actions management sections"
				>
					{/*
					 * AC1 — "each showing how many records it holds". The counts are the RECORD
					 * totals, not the page length: `allActionRows` is the whole set and
					 * `groupTotalCount` is the server's total, so changing rows-per-page cannot
					 * move them (case C17734).
					 */}
					{/*
					 * The icons and sentence case come from actions-01..06, which lead each tab with
					 * a glyph (bolt for actions, a grid for groups) and do NOT shout the label.
					 *
					 * The count stays in the LABEL as "(N)" rather than moving into the badge chip
					 * the design draws. That is deliberate: the tab's accessible name is what
					 * C17734 asserts — `Actions (25)` / `Groups (3)`, proving the counts are record
					 * totals and not page lengths — and a chip would make the name read "Actions 25".
					 * Rewriting a passing, AC-linked assertion to accommodate a cosmetic change is
					 * the wrong way round, so the chip is left to the design-gaps note instead.
					 */}
					<Tab
						value="actions"
						icon={<BoltIcon fontSize="small" />}
						iconPosition="start"
						label={`Actions (${allActionRows.length})`}
						sx={metrics.tabItem}
					/>
					<Tab
						value="groups"
						icon={<GridViewOutlinedIcon fontSize="small" />}
						iconPosition="start"
						label={`Groups (${groupTotalCount})`}
						sx={metrics.tabItem}
					/>
				</Tabs>
				<Box sx={{ flex: 1 }} />
				<Typography
					sx={metrics.tabHint}
					data-testid="actions-permission-caption"
				>
					{activeTab === "actions"
						? "Every action is one permission a role can grant."
						: "Groups are the sections a role administrator sees when granting permissions."}
				</Typography>
			</Box>
			{activeTab === "actions" ? renderActionsTab() : renderGroupsTab()}
			{/*
			 * Mounted only while open, as Payers does: the modal fires its audit-log read on
			 * mount, so keeping it mounted would query the endpoint on every visit to the screen
			 * whether or not anyone asked for the log.
			 */}
			{/*
			 * SIMS-8734 AC5 (FR-4) — one discard prompt, reached from the tab switch.
			 *
			 * Worded like the editor's (ConfigEditorShell) so the two read as the same question:
			 * Keep editing is the safe default and comes first, so a hurried Enter cannot destroy
			 * the reorder.
			 */}
			<Dialog
				open={pendingTabSwitch !== null}
				onClose={() => setPendingTabSwitch(null)}
				PaperProps={{
					sx: {
						width: "27.5rem",
						maxWidth: "100%",
						m: 2,
						borderRadius: "0.75rem",
					},
				}}
			>
				<DialogTitle
					sx={{
						padding: "1.25rem 1.5rem 0.5rem",
						fontSize: "1.125rem",
						fontWeight: 700,
					}}
				>
					Discard unsaved changes?
				</DialogTitle>
				<DialogContent sx={{ padding: "0 1.5rem 1rem" }}>
					<Typography sx={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}>
						The new section order has not been saved. Discarding leaves the
						groups in the order role administrators see now.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ padding: "0.75rem 1.5rem 1.25rem", gap: 1.5 }}>
					<Button variant="outlined" onClick={() => setPendingTabSwitch(null)}>
						Keep editing
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={() => {
							const next = pendingTabSwitch;
							cancelGroupOrder();
							if (next) setActiveTab(next);
						}}
					>
						Discard changes
					</Button>
				</DialogActions>
			</Dialog>
			{actionPendingDelete &&
				(() => {
					const granting = grantingRoleNames(
						rolesQuery.data?.body,
						actionPendingDelete._id,
					);
					return (
						<ConfigDeleteDialog
							open
							recordName={actionPendingDelete.code}
							recordNoun="action"
							// AC1 / FR-16: a granted action is not deletable, and the refusal names
							// the roles so the recovery is actionable. Empty list -> the dialog
							// renders its allowed state.
							dependencies={
								granting.length > 0
									? [
											{
												kind: "roles",
												label: "roles",
												count: granting.length,
												names: granting,
											},
										]
									: []
							}
							blockedSuggestion="Remove it from those roles before deleting."
							// AC2 / FR-8: an action IS gone, so the sentence says so — the shell's
							// default wording ("removed from the list ... nothing already recorded
							// changes") is true of a payer and not of a permission.
							allowedBody={`${actionPendingDelete.code} will be permanently deleted. This cannot be undone. No roles grant this action.`}
							// Withheld while the roles are still loading: without them the screen
							// cannot tell a safe delete from one that revokes live access, and
							// guessing in that gap is what FR-16 exists to prevent.
							isCheckingDependencies={rolesQuery.isLoading}
							dependenciesUnavailable={rolesQuery.isError}
							isDeleting={deleteActionMutation.isPending}
							onConfirm={() => void confirmDeleteAction()}
							onClose={() => setActionPendingDelete(null)}
						/>
					);
				})()}
			{openAuditLogModal ? (
				<AuditLogModal
					open={openAuditLogModal}
					module="Actions"
					onClose={() => setOpenAuditLogModal(false)}
				/>
			) : null}
		</Box>
	);
};

export default ActionsComponent;
